<!--
  The sidebar as a bottom sheet on a phone, less the places of the tab bar
  (see sheetMain in useSidebarNavigation). It closes on a tap outside, on
  Escape and on every page visit.
-->
<template>
  <Teleport v-if="isTeleportReady" to="#teleported">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isMoreSheetOpen"
        class="fixed inset-0 z-50 flex items-end bg-neutral-900/40 md:hidden"
        @click.self="isMoreSheetOpen = false"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="More of GrowthMentor"
          class="max-h-[88dvh] w-full overflow-y-auto overscroll-contain rounded-t-3xl bg-white px-2 pt-2 pb-[env(safe-area-inset-bottom)]"
        >
          <FocusLoop :is-active="isMoreSheetOpen">
            <div
              class="mx-auto mt-1 mb-2 h-1.5 w-10 rounded-full bg-neutral-200"
              aria-hidden="true"
            />
            <AppSidebar
              variant="sheet"
              :counts="counts"
              :mark-messages-as-read="markMessagesAsRead"
              :mark-notifications-as-seen="markNotificationsAsSeen"
            />
          </FocusLoop>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import FocusLoop from '@/shared/components/FocusLoop.vue'
import AppSidebar from './AppSidebar.vue'
import { useSidebarState } from './useSidebarState'
import type { SidebarCounts } from './useSidebarCounts'

defineProps<{
  counts: SidebarCounts
  markMessagesAsRead: () => void
  markNotificationsAsSeen: () => void
}>()

const { isMoreSheetOpen } = useSidebarState()
const isTeleportReady = ref(false)

const closeOnEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape') isMoreSheetOpen.value = false
}

let removeNavigateListener: (() => void) | undefined
let openedFrom: HTMLElement | null = null

// While the sheet is open the page behind it does not scroll, and focus
// goes back to the button that opened it when it closes.
watch(isMoreSheetOpen, (isOpen) => {
  if (isOpen) {
    openedFrom = document.activeElement as HTMLElement | null
    document.body.style.overflow = 'hidden'
    return
  }
  document.body.style.overflow = ''
  openedFrom?.focus?.()
  openedFrom = null
})

onMounted(() => {
  isTeleportReady.value = !!document.getElementById('teleported')
  document.addEventListener('keydown', closeOnEscape)
  removeNavigateListener = router.on('navigate', () => {
    isMoreSheetOpen.value = false
  })
})

onBeforeUnmount(() => {
  isMoreSheetOpen.value = false
  document.body.style.overflow = ''
  document.removeEventListener('keydown', closeOnEscape)
  removeNavigateListener?.()
})
</script>
